import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { FaCheck } from "react-icons/fa";
import AppointmentCancellationModal from "@/components/appointment-cancellation-modal";
import AppointmentNoShowConfirmationModal from "@/components/appointment-no-show-confirmation-modal";
import { generateAppointmentCancellationMessage, getEventCancellationTemplate } from "@/components/custom-calendar/utils";
import QuickNoteCard from "@/components/quick-note-card";
import { Badge, Label, Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui";
import IdDisplay from "@/components/ui/id-display";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useLocationTimezoneOf } from "@/hooks/use-timezone";
import { formatToHumanReadable } from "@/lib/format-data";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { clickableText, cx } from "@/lib/tailwind-utils";
import { toMs } from "@/lib/timestamp";
import { useToast } from "@/lib/use-toast";
import { EventIcon, EventSegmentType, EventStatus } from "@/types/enums/event";
import { RecurrenceModificationMode } from "@/types/enums/recurrence-modification-mode";
import { UserType } from "@/types/enums/user-type";
import { EventDetail, getServiceSegments } from "@/types/event";
import { QuickNoteSummary } from "@/types/quick-note";
import { AppointmentChecklistComponent } from "./appointment-checklist";
import { appointmentChecklistSkeleton } from "./appointment-checklist";
import { AppointmentItem } from "./appointment-item";
import { selectPatientSidebar } from "@/features/sidebar/patient-sidebar";
import { eventQueryKeys, eventQueryOptions, useUpdateEventStatusMutation } from "@/query/check-in/calendar/event-query";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
import { useWaitlistCancellationStore } from "@/stores/waitlist-cancellation-store";
interface TodaysAppointmentsProps {
  eventId: string;
  /**
   * Optional fully-hydrated event to seed the detail cache with. When provided
   * (e.g. from the sidebar's `events-for-patient` list, which already returns
   * the identical `EventDetail`), the per-row `detail/{id}` fetch is skipped —
   * avoiding a request-per-appointment fan-out. Omit it to fetch by id as usual.
   */
  event?: EventDetail;
  className?: string;
  handleViewNote?: (eventId?: string, noteId?: string) => void;
  children?: ReactNode;
}
interface TodaysAppointmentsContextValue {
  event: EventDetail;
  handleViewNote?: (eventId?: string, noteId?: string) => void;
}
const TodaysAppointmentsContext = createContext<TodaysAppointmentsContextValue | null>(null);
function useTodaysAppointmentsContext() {
  const context = useContext(TodaysAppointmentsContext);
  if (!context) {
    throw new Error("TodaysAppointments.Checklist must be rendered inside TodaysAppointments");
  }
  return context;
}
function TodaysAppointmentChecklist() {
  const $ = _c(3);
  const {
    event,
    handleViewNote
  } = useTodaysAppointmentsContext();
  if (event.status === EventStatus.CANCELLED) {
    return null;
  }
  let t0;
  if ($[0] !== event || $[1] !== handleViewNote) {
    t0 = <AppointmentChecklistComponent event={event} handleViewNote={handleViewNote} />;
    $[0] = event;
    $[1] = handleViewNote;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export function TodaysAppointments({
  eventId,
  event: initialEvent,
  className,
  handleViewNote,
  children
}: TodaysAppointmentsProps) {
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    patientTiny
  } = useRightSidebarStore(selectPatientSidebar);
  const {
    prepare,
    complete,
    dismiss
  } = useWaitlistCancellationStore();
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const canCompleteManually = hasPermission(currentUser, PERMISSIONS.SCHEDULING_APPOINTMENTS_COMPLETE);
  // Patients read the times on the appointment's location clock.
  const locationTimezoneOf = useLocationTimezoneOf();
  const updateEventStatusMutation = useUpdateEventStatusMutation();
  const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
  const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
  const handleNoteUpdate = useCallback((updatedNote: QuickNoteSummary) => {
    queryClient.setQueryData<EventDetail | null>(eventQueryKeys.detail(eventId), prev => prev ? {
      ...prev,
      quickNotes: prev.quickNotes?.map(n => n.id === updatedNote.id ? updatedNote : n)
    } : prev);
  }, [queryClient, eventId]);
  const handleNoteDelete = useCallback((noteId: string) => {
    queryClient.setQueryData<EventDetail | null>(eventQueryKeys.detail(eventId), prev_0 => prev_0 ? {
      ...prev_0,
      quickNotes: prev_0.quickNotes?.filter(n_0 => n_0.id !== noteId)
    } : prev_0);
  }, [queryClient, eventId]);
  const sortEvent = useCallback((event: EventDetail | null): EventDetail | null => {
    if (!event) return null;
    const serviceSegments = getServiceSegments(event.segments || []);
    const sortedSegments = [...serviceSegments].sort((a, b) => a.start && b.start ? toMs(a.start) - toMs(b.start) : 0);
    const otherSegments = (event.segments || []).filter(s => s.type !== EventSegmentType.SERVICE);
    return {
      ...event,
      segments: [...otherSegments, ...sortedSegments]
    };
  }, []);
  const {
    data: event_0,
    isLoading
  } = useQuery({
    ...eventQueryOptions.detail(eventId),
    // Seed from the list payload when available so the row renders without a
    // per-row `detail/{id}` fetch. `initialData` alone (no `initialDataUpdatedAt`)
    // is stamped fresh, so the 5-min default staleTime prevents a refetch on
    // mount — the request-per-appointment fan-out is eliminated.
    initialData: initialEvent,
    select: sortEvent
  });

  // Extract patientId from event attendees (for patient page) or fall back to store (for calendar page)
  const patientId = useMemo(() => {
    const patientAttendee = event_0?.attendees?.find(attendee => attendee.user.type === UserType.PATIENT);
    return patientAttendee?.user.id || patientTiny?.id;
  }, [event_0?.attendees, patientTiny?.id]);
  const isFirstAppointment = useMemo(() => {
    return event_0?.icons?.includes(EventIcon.NEW_PATIENT);
  }, [event_0?.icons]);
  const handleStatusUpdate = async (newStatus: EventStatus) => {
    if (!event_0) return;

    // Special handling for cancellation - open modal instead
    if (newStatus === EventStatus.CANCELLED) {
      if (tenant) prepare(event_0, tenant.subDomain);
      setIsCancellationModalOpen(true);
      return;
    }
    if (newStatus === EventStatus.NO_SHOW) {
      setIsNoShowModalOpen(true);
      return;
    }
    updateEventStatusMutation.mutate({
      eventId: event_0.id,
      newStatus,
      patientId
    }, {
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to update appointment status. Please try again.",
          variant: "error"
        });
      }
    });
  };
  const handleNoShowConfirm = async (chargeNoShowFee?: boolean, sendSms?: boolean) => {
    if (!event_0) return;
    updateEventStatusMutation.mutate({
      eventId: event_0.id,
      newStatus: EventStatus.NO_SHOW,
      patientId,
      chargeNoShowFee,
      sendSms
    }, {
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to update appointment status. Please try again.",
          variant: "error"
        });
      }
    });
  };
  const handleCancellationConfirm = async (cancellationReason: string, sendSms_0: boolean, cancellationMessage?: string, recurrenceModificationMode?: RecurrenceModificationMode, chargeCancellationFee?: boolean) => {
    if (!event_0) return;
    const cancelledEvent = event_0;
    const tenantAtCancelTime = tenant;
    try {
      const result = await updateEventStatusMutation.mutateAsync({
        eventId: cancelledEvent.id,
        newStatus: EventStatus.CANCELLED,
        patientId,
        cancellationReason,
        sendSms: sendSms_0,
        cancellationMessage,
        recurrenceModificationMode,
        chargeCancellationFee
      });
      if (result && tenantAtCancelTime) {
        complete(cancelledEvent, tenantAtCancelTime.subDomain);
      } else {
        dismiss();
      }
      setIsCancellationModalOpen(false);
    } catch {
      dismiss();
      toast({
        title: "Error",
        description: "Failed to cancel appointment. Please try again.",
        variant: "error"
      });
    }
  };
  if (isLoading) {
    return <div className={cx("mb-4 mt-2", className)}>
        <div className="h-4 w-56 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-3 w-40 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-3 w-36 animate-pulse rounded bg-muted" />
        {appointmentChecklistSkeleton("mt-2")}
      </div>;
  }
  if (!event_0) return null;

  // Empty until the location's timezone is known. The cancellation modal waits
  // for it, so the default SMS is never written without the appointment's
  // date and time and then swapped in under someone typing.
  const eventTimezone = locationTimezoneOf(event_0.location?.id);
  return <div className={cx("mb-4 mt-2", className)}>
      <AppointmentItem appointment={event_0} showDetailedTime={true} showDate={false} showEdit={true} className="mb-0" />

      {/* Event ID */}
      <div className="mt-2 text-caption text-foreground-secondary">
        <IdDisplay id={event_0.id} label="Event ID" truncate={false} />
      </div>

      {/* First Appointment Badge */}
      {isFirstAppointment && <div className="mt-2">
          <Badge variant="success" className="text-caption">
            First Appointment
          </Badge>
        </div>}

      {/* Quick Note Section */}
      {event_0.quickNotes && event_0.quickNotes.length > 0 && <div className="mt-3 space-y-2">
          {event_0.quickNotes.map(note => <QuickNoteCard key={note.id} note={note} size="sm" event={event_0} onNoteUpdate={handleNoteUpdate} onNoteDelete={handleNoteDelete} />)}
        </div>}

      {children ? <TodaysAppointmentsContext.Provider value={{
      event: event_0,
      handleViewNote
    }}>
          {children}
        </TodaysAppointmentsContext.Provider> : null}

      {event_0.status === EventStatus.COMPLETED ? <>
          <div className="mt-3 flex w-fit items-center rounded-md bg-status-success-bg px-3 py-2 text-body">
            <FaCheck className="mr-2 h-4 w-4 text-status-success-text" />
            <div className="flex flex-col">
              <p className="font-medium text-status-success-text">
                This appointment has been completed!
              </p>
              <div className={cx(clickableText, "text-caption")} onClick={() => handleStatusUpdate(EventStatus.PENDING)}>
                Switch back to pending
              </div>
            </div>
          </div>
        </> : <>
          <Label className="mt-4">Appointment Status</Label>
          <p className="text-left text-caption text-muted-foreground">
            Once all mandatory steps are completed, the appointment status will
            automatically be changed to completed
          </p>
          <Select value={event_0.status} onValueChange={value => handleStatusUpdate(value as EventStatus)}>
            <SelectTrigger className="mt-2 w-28 px-2 py-1 text-caption">
              {formatToHumanReadable(event_0.status)}
            </SelectTrigger>
            <SelectContent>
              {Object.values(EventStatus).filter(statusOption => statusOption !== EventStatus.RESERVED && statusOption !== EventStatus.OVERRIDABLE && (canCompleteManually || statusOption !== EventStatus.COMPLETED)).map(statusOption_0 => {
            return <SelectItem key={statusOption_0} value={statusOption_0} className={statusOption_0 === EventStatus.CANCELLED ? "text-status-error-text" : ""}>
                      {formatToHumanReadable(statusOption_0)}
                    </SelectItem>;
          })}
            </SelectContent>
          </Select>
        </>}

      <AppointmentCancellationModal isOpen={isCancellationModalOpen && !!eventTimezone} setIsOpen={open => {
      setIsCancellationModalOpen(open);
      if (!open) dismiss();
    }} onConfirm={handleCancellationConfirm} event={event_0} isLoading={updateEventStatusMutation.isPending} actionType="cancel" defaultCancellationMessage={tenant && eventTimezone ? generateAppointmentCancellationMessage(event_0, tenant.name, eventTimezone) : ""} defaultCancellationMessageFromTemplate={!!getEventCancellationTemplate(event_0)} showCancellationFeeToggle autoChargeCardOnFile={tenant?.autoChargeCancellationFees ?? false} />
      <AppointmentNoShowConfirmationModal isOpen={isNoShowModalOpen} setIsOpen={setIsNoShowModalOpen} onConfirm={handleNoShowConfirm} event={event_0} isLoading={updateEventStatusMutation.isPending} showNoShowFeeToggle autoChargeCardOnFile={tenant?.autoChargeNoShowFees ?? false} />
    </div>;
}
TodaysAppointments.Checklist = TodaysAppointmentChecklist;